import React, { useEffect, useRef, useState } from 'react';
import { useNuStreamerContext } from '../NuStreamerContext';
import { useBreakpoints } from '@nutastic/nutastic-react-app';
import NuStreamerVideo from './NuStreamerVideo';
import classnames from 'classnames';
import { PauseCircleFilled, PlayCircleFilled, StepBackwardOutlined, StepForwardOutlined } from '@ant-design/icons';
import useWindowSize from '../../../hooks/useWindowSize';
import { useNavigation } from '../../../hooks/useNavigation';

const componentClass = 'nuVideoPlayerCompact';

const PLAYER_DIMENSIONS = {
    SMALL: { width: 200, height: 112 },
    LARGE: { width: 360, height: 202 }
}

const VideoPlayerCompact = ({}) => {
    const compactVideoRef = useRef(null);
    const [isFullScreenMode, setIsFullScreenMode] = useState(false);
    const { loadPage } = useNavigation();
    const { lg: isLargeScreen, md: isMediumScreen } = useBreakpoints();
    const {
        timeProgress,
        stopVideoPlayer,
        showCompactVideo,
        activeVideo,
        onUpdateMediaSession,
        isPlayingVideo,
        duration
    } = useNuStreamerContext();
    const { windowWidth } = useWindowSize();
    const videoFullScreenWidth = isMediumScreen ? PLAYER_DIMENSIONS.LARGE.width : windowWidth;
    const videoFullScreenHeight = isMediumScreen ? PLAYER_DIMENSIONS.LARGE.height : (videoFullScreenWidth / (16/9));

    const { title, contentArtist, image, slug } = activeVideo?.config || {};
    const artistNames = [];
    (contentArtist || []).forEach(a => artistNames.push(a?.name));

    const classes = classnames(componentClass, 'animated', {
        slideInRight: !isFullScreenMode,
        fadeInUp: isFullScreenMode,
        fullScreenMode: isFullScreenMode
    });

    useEffect(() => {
        const thisVideo = compactVideoRef.current;
        if (thisVideo && showCompactVideo) {
            thisVideo.someoneIsSeeking = true;
            thisVideo.currentTime = timeProgress;
            onUpdateMediaSession();
            //force video to play since we know we are in compact mode
            compactVideoRef?.current?.play();
        }
    }, [compactVideoRef]);

    const onClosePlayerClick = () => {
        stopVideoPlayer();
    };

    const collapseCompactPlayer = () => {
        if (compactVideoRef.current) {
            compactVideoRef.current.keepOpenOnEnd = false;
        }
        setIsFullScreenMode(false);
    }

    const onPlayOrPause = () => {
        if (compactVideoRef?.current?.paused) {
            compactVideoRef?.current?.play();
        } else {
            compactVideoRef?.current?.pause();
        }
    };

    const onVideoClick = () => {
        if (!isFullScreenMode) {
            compactVideoRef.current.keepOpenOnEnd = true;
            setIsFullScreenMode(true);
        }
    };

    const onRewind = () => {
        if(compactVideoRef?.current) {
            const currentTime = compactVideoRef.current.currentTime;
            compactVideoRef.current.currentTime = (currentTime - 10) > 0 ? currentTime - 10 : 0;
        }
    };

    const onForward = () => {
        if(compactVideoRef?.current) {
            const currentTime = compactVideoRef.current.currentTime;
            compactVideoRef.current.currentTime = (currentTime + 10) < duration ? currentTime + 10 : duration;
        }
    };

    const onGoToClicked = () => {
        collapseCompactPlayer();
        loadPage(`/content/video/${slug}`);
    };

    return showCompactVideo ? <div className={classes}>
        <div className={`${componentClass}__actions`}>
            { (!isFullScreenMode || isFullScreenMode && !isPlayingVideo) && <i className={isFullScreenMode ? "fa fa-times-circle" : "fa fa-close"} onClick={onClosePlayerClick} /> }
            { (isFullScreenMode && isPlayingVideo) && <i className="fa fa-arrow-circle-down" onClick={collapseCompactPlayer} /> }
        </div>
        <NuStreamerVideo
            width={ isFullScreenMode ? videoFullScreenWidth : isLargeScreen ? PLAYER_DIMENSIONS.LARGE.width : PLAYER_DIMENSIONS.SMALL.width }
            height={ isFullScreenMode ? videoFullScreenHeight : isLargeScreen ? PLAYER_DIMENSIONS.LARGE.height : PLAYER_DIMENSIONS.SMALL.height }
            playsInline={true}
            usePip={false}
            useControls={false}
            elementRef={compactVideoRef}
            onVideoClick={onVideoClick}
        >
            <source src={activeVideo.videoSource} />
        </NuStreamerVideo>
        { isFullScreenMode && <div className={`${componentClass}__info`}>
            <h3>{ title || 'Unknown Title' }</h3>
            <span>{ artistNames.join(", ") }</span>
        </div> }
        { isFullScreenMode && <div className={`${componentClass}__controls`}>
            <span onClick={onRewind}><StepBackwardOutlined /></span>
            <span onClick={onPlayOrPause}>{ isPlayingVideo ? <PauseCircleFilled /> : <PlayCircleFilled /> }</span>
            <span onClick={onForward}><StepForwardOutlined /></span>
        </div> }
        { isFullScreenMode && <div className={`${componentClass}__toGoBtn`}>
            <button className="btn btn-dark-red" onClick={onGoToClicked}>Go To Video</button>
        </div> }
    </div> : null;
}

export default VideoPlayerCompact;
